import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../../firebase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, LogOut, FileText, Trash2, Edit, Save, X, Upload, ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";

const AdminDashboard = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "",
    date: new Date().toISOString().split("T")[0],
    readTime: "5 min read",
    image: "",
    slug: "",
  });
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast({
        title: "Invalid file",
        description: "Please choose a JPEG, PNG, GIF, or WebP image.",
        variant: "destructive",
      });
      return;
    }
    setImageUploading(true);
    try {
      const filename = `${Date.now()}_${file.name.replace(/\s/g, "_")}`;
      const storageRef = ref(storage, `blogs/${filename}`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setFormData((prev) => ({ ...prev, image: downloadUrl }));
      toast({ title: "Image uploaded", description: "Blog image is ready." });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast({
        title: "Upload failed",
        description: error?.message || "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleChangeImage = () => {
    setFormData((prev) => ({ ...prev, image: "" }));
    fileInputRef.current?.click();
  };

  // Check if user is admin (optional - you can enable this later)
  const checkAdminAccess = async (userEmail) => {
    try {
      // Option 1: Check against admins collection in Firestore
      const adminDoc = await getDoc(doc(db, "admins", userEmail));
      if (adminDoc.exists()) {
        return true;
      }
      
      // Option 2: For now, allow any authenticated user (remove this in production)
      // You can add a whitelist of admin emails here
      const adminEmails = [
        "admin@thetechbus.com",
        // Add more admin emails here
      ];
      
      return adminEmails.includes(userEmail);
    } catch (error) {
      console.error("Error checking admin access:", error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/admin/login");
      } else {
        // Optional: Uncomment to enable admin role verification
        // const isAdmin = await checkAdminAccess(user.email);
        // if (!isAdmin) {
        //   toast({
        //     title: "Access Denied",
        //     description: "You don't have admin privileges",
        //     variant: "destructive",
        //   });
        //   signOut(auth);
        //   navigate("/admin/login");
        //   return;
        // }
        fetchBlogs();
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchBlogs = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "blogs"));
      const blogsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBlogs(blogsData);
    } catch (error) {
      console.error("Error fetching blogs:", error);
      toast({
        title: "Error",
        description: "Failed to fetch blogs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const blogData = {
        ...formData,
        slug: formData.slug || generateSlug(formData.title),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      if (editingId) {
        await updateDoc(doc(db, "blogs", editingId), {
          ...blogData,
          updatedAt: new Date().toISOString(),
        });
        toast({
          title: "Success",
          description: "Blog updated successfully",
        });
      } else {
        await addDoc(collection(db, "blogs"), blogData);
        toast({
          title: "Success",
          description: "Blog created successfully",
        });
      }

      setFormData({
        title: "",
        excerpt: "",
        content: "",
        category: "",
        date: new Date().toISOString().split("T")[0],
        readTime: "5 min read",
        image: "",
        slug: "",
      });
      setEditingId(null);
      setShowForm(false);
      fetchBlogs();
    } catch (error) {
      console.error("Error saving blog:", error);
      toast({
        title: "Error",
        description: "Failed to save blog",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (blog) => {
    setFormData({
      title: blog.title || "",
      excerpt: blog.excerpt || "",
      content: blog.content || "",
      category: blog.category || "",
      date: blog.date || new Date().toISOString().split("T")[0],
      readTime: blog.readTime || "5 min read",
      image: blog.image || "",
      slug: blog.slug || "",
    });
    setEditingId(blog.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this blog?")) {
      return;
    }

    try {
      await deleteDoc(doc(db, "blogs", id));
      toast({
        title: "Success",
        description: "Blog deleted successfully",
      });
      fetchBlogs();
    } catch (error) {
      console.error("Error deleting blog:", error);
      toast({
        title: "Error",
        description: "Failed to delete blog",
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold gradient-text">Admin Dashboard</h1>
            <Button onClick={handleLogout} variant="outline" className="gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="blogs" className="space-y-6">
          <TabsList>
            <TabsTrigger value="blogs">
              <FileText className="w-4 h-4 mr-2" />
              Blogs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="blogs" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Blog Management</h2>
              <Button onClick={() => {
                setShowForm(true);
                setEditingId(null);
                setFormData({
                  title: "",
                  excerpt: "",
                  content: "",
                  category: "",
                  date: new Date().toISOString().split("T")[0],
                  readTime: "5 min read",
                  image: "",
                  slug: "",
                });
              }} className="gap-2">
                <Plus className="w-4 h-4" />
                Add New Blog
              </Button>
            </div>

            {showForm && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{editingId ? "Edit Blog" : "Create New Blog"}</CardTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setShowForm(false);
                        setEditingId(null);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="title">Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="slug">Slug</Label>
                        <Input
                          id="slug"
                          value={formData.slug}
                          onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                          placeholder="Auto-generated from title"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="category">Category *</Label>
                        <Input
                          id="category"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="date">Date *</Label>
                        <Input
                          id="date"
                          type="date"
                          value={formData.date}
                          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="readTime">Read Time</Label>
                        <Input
                          id="readTime"
                          value={formData.readTime}
                          onChange={(e) => setFormData({ ...formData, readTime: e.target.value })}
                          placeholder="5 min read"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Blog Image</Label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        {formData.image ? (
                          <div className="space-y-2">
                            <div className="relative rounded-lg overflow-hidden border border-border bg-muted/50 max-w-sm aspect-video">
                              <img
                                src={formData.image}
                                alt="Blog preview"
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={handleChangeImage}
                                disabled={imageUploading}
                                className="gap-2"
                              >
                                <ImageIcon className="w-4 h-4" />
                                Change image
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={imageUploading}
                            className="gap-2 w-full sm:w-auto"
                          >
                            <Upload className="w-4 h-4" />
                            {imageUploading ? "Uploading…" : "Upload image"}
                          </Button>
                        )}
                        <p className="text-xs text-muted-foreground">JPEG, PNG, GIF or WebP. Image is stored on Firebase.</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="excerpt">Excerpt *</Label>
                      <Textarea
                        id="excerpt"
                        value={formData.excerpt}
                        onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                        rows={3}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="content">Content *</Label>
                      <Textarea
                        id="content"
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        rows={10}
                        required
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button type="submit" className="gap-2">
                        <Save className="w-4 h-4" />
                        {editingId ? "Update Blog" : "Create Blog"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowForm(false);
                          setEditingId(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {blogs.length === 0 ? (
                <div className="col-span-full text-center py-12 text-muted-foreground">
                  No blogs found. Create your first blog!
                </div>
              ) : (
                blogs.map((blog) => (
                  <Card key={blog.id} className="flex flex-col overflow-hidden">
                    {blog.image && (
                      <div className="aspect-video w-full overflow-hidden bg-muted">
                        <img src={blog.image} alt="" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg line-clamp-2">{blog.title}</CardTitle>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(blog)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(blog.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                      <CardDescription className="line-clamp-2">{blog.excerpt}</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{blog.category}</span>
                          <span>•</span>
                          <span>{blog.readTime}</span>
                        </div>
                        {blog.date && (
                          <div className="text-sm text-muted-foreground">
                            {new Date(blog.date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
